// src/components/MatchCard.js
import React, { useEffect, useState, useMemo } from 'react';
import TeamBadge from './TeamBadge';
import TeamMatchesModal from './TeamMatchesModal';
import MatchStatisticsModal from './MatchStatisticsModal';
import { useTranslation } from 'react-i18next';
import { FaRobot, FaTimes, FaSearch, FaEye, FaMicrochip, FaChartLine, FaCoins, FaInfoCircle, FaArrowUp } from 'react-icons/fa';
import matchService from '../services/matchService';
import { getTeamColors, deconflictColors, toGrayscale, readableOnLight, rgbToCss } from '../utils/badgeColor';
import { toast } from 'sonner';

// --- EFEKT PSACÍHO STROJE ---
const TypewriterEffect = ({ text, onComplete }) => {
	const [displayedText, setDisplayedText] = useState('');

	useEffect(() => {
		setDisplayedText('');
		let currentIndex = 0;

		const intervalId = setInterval(() => {
			if (currentIndex < text.length) {
				currentIndex++;
				setDisplayedText(text.slice(0, currentIndex));
			} else {
				clearInterval(intervalId);
				if (onComplete) onComplete();
			}
		}, 15);

		return () => clearInterval(intervalId);
	}, [text, onComplete]);

	return <span className="typewriter-text">{displayedText}</span>;
};
// ---------------------------------------------------------

// ---------------------------------------------------------

const MatchCard = ({
	match,
	firstLegMatch,
	prediction,
	onInputChange,
	onSubmitPrediction,
	isPast,
	isLoading = false,
	isDisabled = false,
	isScoutMode,
	onScoutUsed,
	isPredictionMode = false,
	onPredictionSelected,
	hasPrediction = null,
	predictionUnlocked = false,
	canUsePredictionCredit = true,
	isOddsMode = false,
	onOddsSelected,
	hasOdds = null,
	tournamentId,
	isInternational,
	isMember = true,
	scoringType,
	statisticsSummary = null,
	leagueId = null
}) => {
	const { t } = useTranslation();
	const [teamModalInfo, setTeamModalInfo] = useState(null);
	const [hasRendered, setHasRendered] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [showTooltip, setShowTooltip] = useState(false);
	const [hoveredTendency, setHoveredTendency] = useState(null);

	const [loadedAnalysis, setLoadedAnalysis] = useState(null);
	const [isAnalysisVisible, setIsAnalysisVisible] = useState(false);
	const [isAnalyzing, setIsAnalyzing] = useState(false);

	const [statsModalOpen, setStatsModalOpen] = useState(false);

	// Dostupnost statistik i počty červených karet dostáváme z hromadného souhrnu,
	// který pro celý hrací den načte LeagueDetails jedním requestem. Celý JSON se
	// stahuje až po otevření modálu.
	const redCards = {
		home: statisticsSummary?.homeRedCards ?? 0,
		away: statisticsSummary?.awayRedCards ?? 0
	};

	// Dynamické pozadí ve stylu Apple Sports: z log obou týmů vytáhneme jejich
	// dominantní barvu a vložíme ji do karty jako CSS proměnné (--home-rgb /
	// --away-rgb). CSS pak postaví tmavý gradient se „září" domácích vlevo a
	// hostů vpravo. Barvy jsou cachované (sdílené s TeamBadge), takže se každé
	// logo zpracuje jen jednou.
	const [bgColors, setBgColors] = useState(null);

	const shouldTranslateTeams = isInternational === true;

	// COMPETITIVE liga: pod inputy ukazujeme body za tendenci (výhra domácích /
	// remíza / výhra hostů), které backend spočítal z kurzů. Casual ligy beze změny.
	const isCompetitive = (scoringType || '').toUpperCase() === 'COMPETITIVE';

	// COMPETITIVE liga: dokud backend nespočítal z kurzů body za jednotlivé
	// tendence (výhra domácích / remíza / výhra hostů), tip nelze podat –
	// hráč by tipoval naslepo bez znalosti bodového ohodnocení. Zamkneme
	// inputy i tlačítko; backend má stejný guard pro jistotu.
	const competitiveTipsLocked = isCompetitive
		&& (typeof match.homeTeamPoints !== 'number'
			|| typeof match.awayTeamPoints !== 'number'
			|| typeof match.drawPoints !== 'number');

	useEffect(() => {
		setHasRendered(true);
	}, []);

	useEffect(() => {
		let cancelled = false;
		Promise.all([
			getTeamColors(match.homeTeam),
			getTeamColors(match.awayTeam),
		]).then(([home, away]) => {
			if (cancelled) return;
			// Když mají oba týmy příliš blízké dominantní barvy, deconflictColors
			// sáhne po sekundární barvě (nebo rozprostře tóny), ať pozadí není nudné.
			const { home: homeColor, away: awayColor, adjusted } = deconflictColors(home.palette, away.palette);
			setBgColors({ home: homeColor, away: awayColor, adjusted });
		});
		return () => { cancelled = true; };
	}, [match.homeTeam.id, match.homeTeam.name, match.awayTeam.id, match.awayTeam.name]);

	useEffect(() => {
		if (match.aiAnalysis) {
			setLoadedAnalysis(match.aiAnalysis);
		}
	}, [match.aiAnalysis]);

	const formatTime = (utcDate) => {
		if (!utcDate) return '';
		const date = new Date(utcDate);
		const hours = date.getHours().toString().padStart(2, '0');
		const minutes = date.getMinutes().toString().padStart(2, '0');
		return `${hours}:${minutes}`;
	};

	const getDaysUntilMatch = () => {
		const matchDate = new Date(match.date);
		const now = new Date();
		const diffTime = matchDate - now;
		return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
	};

	const daysUntil = getDaysUntilMatch();
	const isTooFarInFuture = daysUntil > 14;

	const handleRevealAnalysis = async (e) => {
		e.stopPropagation();
		if (isAnalyzing || isPast) return;

		setIsAnalyzing(true);
		try {
			const result = await matchService.getMatchAnalysis(match.id, tournamentId);
			if (result === 'AI_INACTIVE_TEAMS') {
				toast.warning(t('AI_INACTIVE_TEAMS'));
				return;
			}
			setLoadedAnalysis(result);
			setIsAnalysisVisible(true);
			if (onScoutUsed) onScoutUsed();
		} catch (error) {
			if (error.response) {
				const status = error.response.status;
				const message = error.response.data;

				if (status === 403) {
					toast.warning(message || t('matchCard.dailyLimitReached'));
				} else if (status === 400) {
					toast.info(message || t('matchCard.tooEarlyForMatch'));
				} else {
					toast.error(t('matchCard.analysisLoadFailed'));
				}
			} else {
				console.error(error);
				toast.error(t('matchCard.connectionError'));
			}
		} finally {
			setIsAnalyzing(false);
		}
	};

	// Klik na ikonku robota (pravý dolní roh) – zobrazí AI analýzu.
	const handleToggleAnalysis = (e) => {
		e.stopPropagation();
		if (loadedAnalysis) {
			setIsAnalysisVisible(true);
		}
	};

	// Klik na tlačítko statistik (levý dolní roh) – otevře modal se statistikami.
	const handleStatsClick = (e) => {
		e.stopPropagation();
		if (isAnalysisVisible) setIsAnalysisVisible(false);
		setStatsModalOpen(true);
	};

	const predictedHomeScore = prediction?.predictedHomeScore ?? '';
	const predictedAwayScore = prediction?.predictedAwayScore ?? '';
	const isPredictionSubmitted = prediction?.id !== undefined;
	const userPoints = prediction?.points;

	// Bodový násobič kola (backend posílá match.multiplier, default 1). Body v
	// prediction.points i váhy na zápase přicházejí ZÁKLADNÍ; násobič uplatňujeme
	// jen pro zobrazení. Základní stupeň (basePoints) slouží ke klasifikaci barvy
	// karty a tooltipu – casual body jsou vždy násobkem base ∈ {0,2,4,7,10}, takže
	// dělení vychází přesně. (U historických tipů obodovaných jiným násobičem může
	// vyjít neceločíselně → gracefully spadne na neutrální styl.)
	const multiplier = Number(match.multiplier) > 1 ? Number(match.multiplier) : 1;
	const basePoints = (typeof userPoints === 'number' && multiplier > 1)
		? userPoints / multiplier
		: userPoints;

	// NOVÉ PROMĚNNÉ PRO LOGIKU ODVETY (SECOND LEG)
	const isFootball = (match.sportType || match.homeTeam?.sportType)?.toUpperCase() === 'FOOTBALL';
	const isSecondLeg = match.secondLeg || match.isSecondLeg;

	// Výpočet, zda tipované skóre v součtu s prvním zápasem znamená celkovou remízu
	const isAggregateDraw = useMemo(() => {
		if (!isSecondLeg || !isFootball || !firstLegMatch || firstLegMatch.scoreHome == null || firstLegMatch.scoreAway == null) {
			return false;
		}
		if (predictedHomeScore === '' || predictedAwayScore === '') {
			return false;
		}

		// První zápas se hrál u dnešních hostů, takže dnešní domácí v něm byli vedeni jako hosté a naopak.
		const aggHome = firstLegMatch.scoreAway + Number(predictedHomeScore);
		const aggAway = firstLegMatch.scoreHome + Number(predictedAwayScore);

		return aggHome === aggAway;
	}, [isSecondLeg, isFootball, firstLegMatch, predictedHomeScore, predictedAwayScore]);

	// Jednorázový fotbalový zápas s možností prodloužení, tipnutý jako remíza po 90 min
	const isSingleLegOvertimeDraw = useMemo(() => {
		if (!isFootball || isSecondLeg || !match.canGoToOvertime) return false;
		if (predictedHomeScore === '' || predictedAwayScore === '') return false;
		return Number(predictedHomeScore) === Number(predictedAwayScore);
	}, [isFootball, isSecondLeg, match.canGoToOvertime, predictedHomeScore, predictedAwayScore]);

	const showAdvancingSelection = isAggregateDraw || isSingleLegOvertimeDraw;

	// COMPETITIVE liga: rozpad získaných bodů na body za tendenci a bonus za přesné
	// skóre. Počítáme jen pro odehraný zápas s tipem a jen pokud backend dodal váhy
	// (homeTeamPoints). Když chybí, necháme fallback na jednotnou hodnotu userPoints.
	const competitiveBreakdown = useMemo(() => {
		if (!isCompetitive || typeof match.homeTeamPoints !== 'number') return null;
		if (match.scoreHome == null || match.scoreAway == null) return null;
		if (!prediction || prediction.predictedHomeScore == null || prediction.predictedAwayScore == null) return null;

		const actualHomeScore = parseInt(match.scoreHome) || 0;
		const actualAwayScore = parseInt(match.scoreAway) || 0;
		const predHomeScore = parseInt(prediction.predictedHomeScore) || 0;
		const predAwayScore = parseInt(prediction.predictedAwayScore) || 0;

		const actualResult = Math.sign(actualHomeScore - actualAwayScore);
		const predResult = Math.sign(predHomeScore - predAwayScore);
		const correctTendency = actualResult === predResult;

		const tendencyPts = correctTendency
			? (actualResult > 0 ? match.homeTeamPoints
				: actualResult < 0 ? match.awayTeamPoints
				: match.drawPoints)
			: 0;

		const isExactHit = predHomeScore === actualHomeScore && predAwayScore === actualAwayScore;
		const exactPts = isExactHit
			? (match.exactScorePoints?.[`${actualHomeScore}:${actualAwayScore}`] ?? 0)
			: 0;

		const outcomeKey = actualResult > 0 ? 'home' : actualResult < 0 ? 'away' : 'draw';

		// Váhy jsou základní → vynásobíme násobičem kola, ať součet badge odpovídá
		// skutečně přiznaným (vynásobeným) bodům.
		return {
			tendencyPts: (typeof tendencyPts === 'number' ? tendencyPts : 0) * multiplier,
			exactPts: exactPts * multiplier,
			isExactHit,
			correctTendency,
			outcomeKey,
		};
	}, [isCompetitive, match.homeTeamPoints, match.awayTeamPoints, match.drawPoints, match.scoreHome, match.scoreAway, match.exactScorePoints, prediction, multiplier]);

	const { homeWinPredictionPercent, drawPredictionPercent, awayWinPredictionPercent } = match;

	const roundPercentage = (percentage) => {
		return Math.round(percentage);
	};

	const handleTeamClick = (team) => {
		if (isLoading || isDisabled || isScoutMode) return;
		setTeamModalInfo({ id: team.id, name: team.name });
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setTeamModalInfo(null);
	};

	const getTooltipText = () => {
		if (!prediction || userPoints === null || typeof userPoints === 'undefined') {
			return t('matchCard.noPrediction');
		}

		if (match.scoreHome === null || match.scoreHome === undefined ||
			match.scoreAway === null || match.scoreAway === undefined) {
			return t('matchCard.matchNotFinished');
		}

		const actualHomeScore = parseInt(match.scoreHome) || 0;
		const actualAwayScore = parseInt(match.scoreAway) || 0;
		const predHomeScore = parseInt(prediction.predictedHomeScore) || 0;
		const predAwayScore = parseInt(prediction.predictedAwayScore) || 0;

		const actualResult = Math.sign(actualHomeScore - actualAwayScore);
		const predictedGoalDifference = predHomeScore - predAwayScore;
		const predictedTotalGoals = predHomeScore + predAwayScore;

		const matchWentToOvertime = match.matchStatus && ['AET', 'PEN', 'AOT', 'AP'].includes(match.matchStatus);
		const predictedOvertime = !!prediction.isDrawInRegularTime || !!prediction.advancingTeamId;

		// COMPETITIVE liga: body = tendence (+ bonus za přesné skóre). Tooltip ukáže rozpad.
		// Pokud na zápasu chybí váhy (homeTeamPoints null), backend skóroval pevnou tabulkou
		// 10/7/4/2 → necháme původní Casual tooltip níže.
		if (isCompetitive && typeof match.homeTeamPoints === 'number') {
			const predResult = Math.sign(predHomeScore - predAwayScore);
			if (actualResult !== predResult) {
				return t('matchCard.competitive.wrongTendency', { defaultValue: 'Špatná tendence – 0 bodů' });
			}

			const winnerText = actualResult === 0
				? t('matchCard.draw')
				: actualResult > 0 ? t('matchCard.homeWin') : t('matchCard.awayWin');
			const rawTendencyPts = actualResult > 0
				? match.homeTeamPoints
				: actualResult < 0 ? match.awayTeamPoints : match.drawPoints;
			// Váhy jsou základní → pro tooltip násobíme násobičem kola.
			const tendencyPts = typeof rawTendencyPts === 'number' ? rawTendencyPts * multiplier : rawTendencyPts;

			let text = t('matchCard.competitive.tendency', { defaultValue: 'Tendence' }) + `: ${winnerText}`;
			if (typeof tendencyPts === 'number') text += ` (+${tendencyPts})`;

			const isExactHit = predHomeScore === actualHomeScore && predAwayScore === actualAwayScore;
			if (isExactHit) {
				const rawBonus = match.exactScorePoints?.[`${actualHomeScore}:${actualAwayScore}`];
				const bonus = typeof rawBonus === 'number' ? rawBonus * multiplier : rawBonus;
				text += ' + ' + t('matchCard.competitive.exactScore', { defaultValue: 'přesné skóre' })
					+ ` (${predHomeScore}:${predAwayScore}`
					+ (typeof bonus === 'number' ? `, +${bonus}` : '')
					+ ')';
			}
			return text;
		}

		// Casual body = základní stupeň (basePoints) × násobič kola. Tooltip
		// vysvětlíme podle základního stupně a když je násobič > 1, připojíme
		// dovětek s výslednou (vynásobenou) hodnotou.
		const withMultiplier = (text) =>
			multiplier > 1
				? text + t('matchCard.multiplierNote', { multiplier, points: userPoints })
				: text;

		if (basePoints === 10) {
			let overtimeText = '';
			if (match.canGoToOvertime && matchWentToOvertime && predictedOvertime) {
				overtimeText = ' + ' + t('matchCard.correctOvertime');
			} else if (match.canGoToOvertime && !matchWentToOvertime && !predictedOvertime) {
				overtimeText = ' + ' + t('matchCard.correctNotOvertime');
			}
			return withMultiplier(t('matchCard.exactResult') + ` (${predHomeScore}:${predAwayScore})${overtimeText}`);
		}

		if (basePoints === 7) {
			const diffText = predictedGoalDifference > 0 ? `+${predictedGoalDifference}` :
				predictedGoalDifference < 0 ? `${predictedGoalDifference}` : '0';
			return withMultiplier(t('matchCard.scoreDifference') + ` (${diffText})`);
		}

		if (basePoints === 4) {
			const winnerText = actualResult === 0 ? t('matchCard.draw') :
				actualResult > 0 ? t('matchCard.homeWin') : t('matchCard.awayWin');
			return withMultiplier(t('matchCard.correctResult') + `: ${winnerText}`);
		}

		if (basePoints === 2) {
			return withMultiplier(t('matchCard.goalsStart') + ` (${predictedTotalGoals}), ` + t('matchCard.goalsEnd'));
		}

		if (basePoints === 0) {
			return t('matchCard.noPoints');
		}

		return `${userPoints} - ` + t('matchCard.unknownValue');
	};

	const statusMap = {
		notStarted: ['NS', 'Not Started', 'TBD'],
		inProgress: ['_1H', 'HT', '_2H', 'ET', 'P', 'P1', 'P2', 'P3', 'OT', 'PT', 'BT', 'INTR'],
		finished: ['FT', 'Finished', 'Match Finished', 'AET', 'PEN', 'AOT', 'AP', 'WO'],
		postponed: ['PST', 'POST']
	};

	let statusClass = '';
	const isPostponed = statusMap.postponed.includes(match.matchStatus);

	if (statusMap.notStarted.includes(match.matchStatus)) statusClass = 'match-status-not-started';
	else if (statusMap.inProgress.includes(match.matchStatus)) statusClass = 'match-status-in-progress';
	else if (statusMap.finished.includes(match.matchStatus)) statusClass = 'match-status-finished';
	else if (isPostponed) statusClass = 'match-status-postponed';

	let pointsClass = '';
	if (competitiveBreakdown) {
		// COMPETITIVE: zlatý okraj karty jen při trefeném přesném skóre (jako Casual).
		pointsClass = competitiveBreakdown.isExactHit ? 'points-ten' : 'points-one';
	} else if (basePoints === 0) pointsClass = 'points-zero';
	else if (basePoints === 2) pointsClass = 'points-two';
	else if (basePoints === 4) pointsClass = 'points-four';
	else if (basePoints === 7) pointsClass = 'points-seven';
	else if (basePoints === 10) pointsClass = 'points-ten';
	else pointsClass = 'points-one';

	const showActualScore = statusMap.inProgress.concat(statusMap.finished).includes(match.matchStatus);
	const isMatchInProgressOrFinished = statusMap.inProgress.includes(match.matchStatus) || statusMap.finished.includes(match.matchStatus);
	const homeWins = isMatchInProgressOrFinished && match.scoreHome > match.scoreAway;
	const awayWins = isMatchInProgressOrFinished && match.scoreAway > match.scoreHome;
	const isNotStarted = statusMap.notStarted.includes(match.matchStatus);
	const isFinished = statusMap.finished.includes(match.matchStatus);

	// Poražený tým (jen u odehraného zápasu) má zešedlé logo – zešedíme i jeho
	// barvu v pozadí (mesh) a stín pod logem, ať to ladí. Stejná logika jako
	// `team-lost` níže: výhra hostů → prohráli domácí a naopak.
	// Remíza u zápasu, který NEMŮŽE jít do prodloužení → nevyhrál ani jeden tým,
	// takže zešedíme loga (i barvy v pozadí/stínu) OBOU týmů. Zápasy s možností
	// prodloužení necháme být – tam o vítězi rozhodne prodloužení/penalty/nájezdy
	// a vítězné logo zůstává barevné (řeší homeWins/awayWins z finálního skóre).
	const isNonOvertimeDraw = isFinished && !match.canGoToOvertime
		&& match.scoreHome != null && match.scoreAway != null
		&& Number(match.scoreHome) === Number(match.scoreAway);

	// Fotbalový zápas rozhodnutý až v prodloužení nebo po penaltách (AET/AOT/PEN/AP).
	// V základní hrací době byla remíza → ani jeden tým „neporazil" druhého v 90 min,
	// takže zešedíme loga OBOU týmů (i jejich barvy v pozadí/stínu) a u postupujícího
	// (vítěze podle penalt, resp. finálního skóre) ukážeme šipku nahoru „postupuje".
	const overtimeFinishStatuses = ['AET', 'AOT', 'PEN', 'AP'];
	const isOvertimeFinish = isFinished && isFootball && overtimeFinishStatuses.includes(match.matchStatus);

	const advancingSide = (() => {
		if (!isOvertimeFinish) return null;
		// Penalty/nájezdy: o postupu rozhoduje skóre z penaltového rozstřelu.
		if ((match.matchStatus === 'PEN' || match.matchStatus === 'AP')
			&& match.penaltyScoreHome != null && match.penaltyScoreAway != null
			&& Number(match.penaltyScoreHome) !== Number(match.penaltyScoreAway)) {
			return Number(match.penaltyScoreHome) > Number(match.penaltyScoreAway) ? 'home' : 'away';
		}
		// Prodloužení: rozhoduje finální skóre po prodloužení.
		if (match.scoreHome != null && match.scoreAway != null
			&& Number(match.scoreHome) !== Number(match.scoreAway)) {
			return Number(match.scoreHome) > Number(match.scoreAway) ? 'home' : 'away';
		}
		return null;
	})();

	const lostSide = isFinished ? (awayWins ? 'home' : (homeWins ? 'away' : null)) : null;
	const greyHomeBadge = lostSide === 'home' || isNonOvertimeDraw || isOvertimeFinish;
	const greyAwayBadge = lostSide === 'away' || isNonOvertimeDraw || isOvertimeFinish;
	const homeBgRgb = (bgColors && greyHomeBadge) ? toGrayscale(bgColors.home) : bgColors?.home;
	const awayBgRgb = (bgColors && greyAwayBadge) ? toGrayscale(bgColors.away) : bgColors?.away;

	// Procenta výhry domácích/hostů obarvíme stejnou barvou, jako má stín pod logem
	// daného týmu. Karta má světlé pozadí, takže světlé odstíny (žlutá, azurová…)
	// přes readableOnLight ztmavíme na čitelný tón. Zešedlý stín poraženého týmu
	// (r==g==b) necháme být – vrátíme undefined a procenta zůstanou výchozí šedá.
	const percentColor = (rgb) => {
		if (!rgb) return undefined;
		const [r, g, b] = rgb;
		if (r === g && g === b) return undefined;
		return rgbToCss(readableOnLight(rgb));
	};
	// U remízou skončeného zápasu nevyhrál ani jeden tým → procenta necháme všechna
	// šedá (stejně jako u remízy), takže žádný tým nevypadá jako vítěz.
	const isFinishedDraw = isFinished && match.scoreHome != null && match.scoreAway != null
		&& Number(match.scoreHome) === Number(match.scoreAway);
	const homePercentColor = isFinishedDraw ? undefined : percentColor(homeBgRgb);
	const awayPercentColor = isFinishedDraw ? undefined : percentColor(awayBgRgb);

	const shouldShowPercentages =
		isMatchInProgressOrFinished &&
		(typeof homeWinPredictionPercent !== 'undefined' ||
			typeof drawPredictionPercent !== 'undefined' ||
			typeof awayWinPredictionPercent !== 'undefined');

	const getButtonText = () => {
		if (isLoading) {
			return isPredictionSubmitted ? t('leagueDetails.updatingTip') : t('leagueDetails.submittingTip');
		}
		return isPredictionSubmitted ? t('leagueDetails.updateTip') : t('leagueDetails.submitTip');
	};

	const isButtonDisabled = isPast || isLoading || isDisabled || isScoutMode || isPredictionMode || isOddsMode || (isMember === false) || competitiveTipsLocked;
	const showScoutOverlay = isScoutMode && !isPast && !loadedAnalysis && !isTooFarInFuture;
	const showPredictionOverlay = isPredictionMode && !isPast;
	const showOddsOverlay = isOddsMode && !isPast;
	const hasAnalysisAvailable = !!loadedAnalysis;
	// Tlačítko statistik v levém dolním rohu ukazujeme jen tehdy, když pro zápas
	// data opravdu existují – podle souhrnu načteného pro celý hrací den.
	const hasStatisticsAvailable = statisticsSummary?.hasStatistics === true && isMatchInProgressOrFinished;
	// Tipy ostatních hráčů ligy jsou viditelné po výkopu (jen pro členy ligy) – i
	// když statistiky nejsou, tlačítko ukážeme a modal otevře rovnou záložku Tipy.
	const canSeeOtherTips = !!leagueId && isMember === true && isMatchInProgressOrFinished;
	const showInfoButton = hasStatisticsAvailable || canSeeOtherTips;

	// Pre-check výsledek: `null` = ještě nevíme (zobrazujeme normální overlay),
	// `false` = backend nemá uložená data → overlay je zašedlý a klik je no-op,
	// `true` = data jsou, klik otevře modal.
	// Predikce není k dispozici, pokud pro zápas chybí data, nebo (při vyčerpaném
	// sdíleném kreditu) pokud ji uživatel ještě nemá odemčenou. Odemčenou predikci
	// lze zobrazit opakovaně zdarma.
	const predictionUnavailable = hasPrediction === false || (!canUsePredictionCredit && !predictionUnlocked);
	const oddsUnavailable = hasOdds === false;

	const handleRevealPrediction = (e) => {
		e.stopPropagation();
		if (predictionUnavailable) return;
		if (onPredictionSelected) onPredictionSelected(match);
	};

	// Klik na indikátor predikce (pravý dolní roh) – znovu zobrazí už odemčenou
	// predikci zdarma. Funguje nezávisle na kreditu, stejně jako re-view AI analýzy.
	const handleRevealUnlockedPrediction = (e) => {
		e.stopPropagation();
		if (onPredictionSelected) onPredictionSelected(match);
	};

	const handleRevealOdds = (e) => {
		e.stopPropagation();
		if (oddsUnavailable) return;
		if (onOddsSelected) onOddsSelected(match);
	};

	const renderPercentage = (percent, color, dimmed) => {
		if (!shouldShowPercentages || typeof percent === 'undefined') return null;
		const style = {};
		if (color) style.color = color;
		// Procenta výsledku, který nenastal (např. při výhře domácích remíza a výhra
		// hostů), zlehka ztlumíme, ať vizuálně vyniká ten, co odpovídá skóre.
		if (dimmed) style.opacity = 0.45;
		return (
			<span className="percentage-value-inline" style={Object.keys(style).length ? style : undefined}>
				{roundPercentage(percent)}%
			</span>
		);
	};

	// Body za tendenci (jen COMPETITIVE liga a jen pokud je backend spočítal).
	// Vlastní tooltip (ne nativní title), aby fungoval i když je karta klikací
	// (dostupná AI analýza). stopPropagation zabrání otevření analýzy klikem na badge.
	// Skutečný výsledek zápasu (jen u rozehraného/odehraného) → podle něj víme, který
	// badge s body za tendenci je relevantní a které body už uživatel nezíská.
	const actualOutcomeKey = (showActualScore && match.scoreHome != null && match.scoreAway != null)
		? (Number(match.scoreHome) > Number(match.scoreAway) ? 'home'
			: Number(match.scoreHome) < Number(match.scoreAway) ? 'away'
			: 'draw')
		: null;

	const renderTendencyPoints = (key, points, title) => {
		if (!isCompetitive || typeof points !== 'number') return null;
		// Badge odpovídající skutečnému výsledku zvýrazníme, pokud uživatel trefil
		// tendenci – zeleně, a zlatě, pokud trefil i přesné skóre.
		const isOutcomeBadge = competitiveBreakdown?.correctTendency && competitiveBreakdown.outcomeKey === key;
		const highlightClass = isOutcomeBadge
			? (competitiveBreakdown.isExactHit ? 'tendency-points-gold' : 'tendency-points-green')
			: '';
		// Rozehraný/odehraný zápas: body za jiné výsledky už nelze získat → ztlumíme je.
		const dimmedClass = (actualOutcomeKey && key !== actualOutcomeKey) ? 'tendency-points-dimmed' : '';
		return (
			<div
				className={`tendency-points ${highlightClass} ${dimmedClass}`}
				onClick={(e) => e.stopPropagation()}
				onMouseEnter={() => setHoveredTendency(key)}
				onMouseLeave={() => setHoveredTendency(null)}
			>
				<span>{points * multiplier}</span>
				{hoveredTendency === key && (
					<div className="tendency-tooltip">{title}</div>
				)}
			</div>
		);
	};

	// Malá červená karta vedle názvu týmu, který v zápase dostal červenou
	// (počet vyčtený ze statistik). U více karet zobrazíme i jejich počet.
	const renderRedCard = (count) => {
		if (!count || count < 1) return null;
		return (
			<span
				className="red-card-badge"
				title={t('matchCard.redCard', { defaultValue: 'Červená karta' })}
				onClick={(e) => e.stopPropagation()}
			>
				{count > 1 && <span className="red-card-count">{count}</span>}
			</span>
		);
	};

	return (
		<>
			<div
				className={`match-card ${statusClass} ${pointsClass} ${hasRendered ? '' : 'with-animation'} ${isLoading ? 'loading' : ''} ${showScoutOverlay ? 'scout-target' : ''} ${showPredictionOverlay ? 'scout-target prediction-target' : ''} ${showOddsOverlay ? 'scout-target odds-target' : ''} ${hasAnalysisAvailable ? 'has-analysis' : ''} ${hasStatisticsAvailable ? 'has-statistics' : ''} ${isCompetitive ? 'competitive' : ''} ${bgColors?.adjusted ? 'colors-adjusted' : ''}`}
				style={bgColors ? {
					'--home-rgb': homeBgRgb.join(', '),
					'--away-rgb': awayBgRgb.join(', '),
				} : undefined}
			>

				<div className="match-time" onClick={(e) => e.stopPropagation()}>{formatTime(match.date)}</div>

				{isFootball && match.canGoToOvertime && isNotStarted && (
					<div className="score-after-90" onClick={(e) => e.stopPropagation()}>
						{t('matchCard.scoreAfter90', { defaultValue: 'Skóre po 90 minutách' })}
					</div>
				)}

				{showInfoButton && (
					<button
						type="button"
						className="analysis-indicator stats-indicator"
						title={hasStatisticsAvailable
							? t('matchCard.showStatistics', { defaultValue: 'Zobrazit statistiky' })
							: t('matchCard.showTips', { defaultValue: 'Zobrazit tipy hráčů' })}
						onClick={handleStatsClick}
					>
						<FaInfoCircle />
					</button>
				)}

				{hasAnalysisAvailable && !isAnalysisVisible && (
					<button
						type="button"
						className="analysis-indicator robot-indicator"
						title={t('matchCard.showAnalysis')}
						onClick={handleToggleAnalysis}
					>
						<FaRobot />
					</button>
				)}

				{/* Ikonka odemčené predikce zůstává i po začátku zápasu – jednou
				    odemčenou predikci si uživatel může kdykoli znovu zobrazit zdarma
				    (stejně jako AI analýzu), takže ji nevážeme na `isPast`. */}
				{predictionUnlocked && (
					<button
						type="button"
						className="analysis-indicator prediction-indicator"
						title={t('showPrediction', { defaultValue: 'Zobrazit predikci' })}
						onClick={handleRevealUnlockedPrediction}
					>
						<FaChartLine />
					</button>
				)}

				{showScoutOverlay && (
					<div className="scout-overlay" onClick={handleRevealAnalysis}>
						<div className="scout-overlay-content">
							{isAnalyzing ? (
								<>
									<FaRobot className="scout-overlay-icon analyzing-icon" />
									<span className="analyzing-text">
										{t('analyse', { defaultValue: 'Analyzuji' })}
										<span className="loading-dots">
											<span>.</span><span>.</span><span>.</span>
										</span>
									</span>
								</>
							) : (
								<>
									<FaRobot className="scout-overlay-icon" />
									<span>{t('analyseMatch')}</span>
								</>
							)}
						</div>
					</div>
				)}

				{showPredictionOverlay && (
					<div
						className={`scout-overlay prediction-overlay ${predictionUnavailable ? 'unavailable-overlay' : ''}`}
						onClick={handleRevealPrediction}
					>
						<div className="scout-overlay-content">
							<FaChartLine className="scout-overlay-icon" />
							<span>
								{predictionUnavailable
									? t('predictionUnavailable', { defaultValue: 'Predikce nedostupná' })
									: t('showPrediction', { defaultValue: 'Zobrazit predikci' })}
							</span>
						</div>
					</div>
				)}

				{showOddsOverlay && (
					<div
						className={`scout-overlay odds-overlay ${oddsUnavailable ? 'unavailable-overlay' : ''}`}
						onClick={handleRevealOdds}
					>
						<div className="scout-overlay-content">
							<FaCoins className="scout-overlay-icon" />
							<span>
								{oddsUnavailable
									? t('oddsUnavailable', { defaultValue: 'Kurzy nedostupné' })
									: t('showOdds', { defaultValue: 'Zobrazit kurzy' })}
							</span>
						</div>
					</div>
				)}

				<div className={(showScoutOverlay || showPredictionOverlay || showOddsOverlay) ? 'blurred-content' : ''}>
					{isSecondLeg && (
						<div style={{
							textAlign: 'center',
							fontSize: '0.7rem',
							color: 'var(--card-text-dim)',
							fontWeight: '400',
							marginTop: '-8px',
							marginBottom: '3px'
						}}>
							{t('matchCard.firstLeg', { defaultValue: '1. zápas' })}: {firstLegMatch && firstLegMatch.scoreHome != null && firstLegMatch.scoreAway != null
								? `${firstLegMatch.scoreAway}:${firstLegMatch.scoreHome}`
								: '- : -'}
						</div>
					)}

					<div className={`score-line ${showAdvancingSelection ? 'has-advancing' : ''}`}>
						<div className="team-side home-side">
							<div
								className={`team-block ${!isLoading && !isDisabled && !isScoutMode ? 'clickable' : ''} ${isFinished && !isOvertimeFinish && (awayWins || isNonOvertimeDraw) ? 'team-lost' : ''}`}
								onClick={(e) => { e.stopPropagation(); handleTeamClick(match.homeTeam); }}
							>
								<TeamBadge
									teamId={match.homeTeam.id}
									teamName={match.homeTeam.name}
									isPostponed={isPostponed}
									isFlag={isInternational === true}
									plain={isInternational !== true}
									shadowColor={homeBgRgb}
									grayscale={isOvertimeFinish}
								/>
								<div className="team-name-row">
									<span className={`team-name ${homeWins ? 'team-name-winner' : ''}`}>
										{shouldTranslateTeams
											? t(`teams.${match.homeTeam.name}`, { defaultValue: match.homeTeam.name })
											: match.homeTeam.name
										}
									</span>
									{advancingSide === 'home' && (
										<span
											className="advancing-arrow"
											title={t('matchCard.advances', { defaultValue: 'Postupuje' })}
											onClick={(e) => e.stopPropagation()}
										>
											<FaArrowUp />
										</span>
									)}
									{renderRedCard(redCards.home)}
								</div>

								{showAdvancingSelection && (
									<div className="advancing-selection" onClick={(e) => e.stopPropagation()}>
										<label className={prediction?.advancingTeamId && prediction.advancingTeamId !== match.homeTeam.id ? 'dimmed' : ''}>
											<input
												type="radio"
												name={`advancing-${match.id}`}
												checked={prediction?.advancingTeamId === match.homeTeam.id}
												onChange={() => !isPast && !isDisabled && onInputChange('advancingTeamId', match.homeTeam.id)}
												disabled={isPast || isDisabled || isScoutMode}
											/>
											{t('matchCard.advances', { defaultValue: 'Postoupí' })}
										</label>
									</div>
								)}
							</div>

							<div className={`score-input-wrapper ${shouldShowPercentages ? 'with-percentages' : 'without-percentages'}`} style={{ marginTop: showAdvancingSelection ? '5px' : '0' }}>
								{renderPercentage(homeWinPredictionPercent, homePercentColor, actualOutcomeKey && actualOutcomeKey !== 'home')}
								<input
									type="number"
									className={`score-input ${statusClass === 'match-status-in-progress'
										? 'in-progress-input'
										: (statusClass === 'match-status-finished' || statusClass === 'match-status-postponed')
											? 'finished-input'
											: ''
										}`}
									value={predictedHomeScore}
									onClick={(e) => e.stopPropagation()}
									onChange={(e) => !isPast && !isDisabled && !competitiveTipsLocked && onInputChange('predictedHomeScore', e.target.value)}
									disabled={isPast || isDisabled || isScoutMode || !isMember || competitiveTipsLocked}
									min="0"
								/>
								{renderTendencyPoints('home', match.homeTeamPoints, t('matchCard.tendencyHome', { defaultValue: 'Body za správně tipnutou výhru domácích' }))}
							</div>
						</div>

						<div className={`actual-score-center ${shouldShowPercentages ? 'with-percentages' : 'without-percentages'}`}>
							{renderPercentage(drawPredictionPercent, undefined, actualOutcomeKey && actualOutcomeKey !== 'draw')}

							{showActualScore ? (
								<div className={`actual-score ${statusClass === 'match-status-in-progress' ? 'in-progress-score' : ''}`}>
									{match.scoreHome} : {match.scoreAway}
								</div>
							) : isPostponed ? (
								<div className="postponed-score">- : -</div>
							) : isNotStarted ? (
								<div className="no-actual-score">:</div>
							) : null}
							{renderTendencyPoints('draw', match.drawPoints, t('matchCard.tendencyDraw', { defaultValue: 'Body za správně tipnutou remízu' }))}
						</div>

						<div className="team-side away-side">
							<div className={`score-input-wrapper ${shouldShowPercentages ? 'with-percentages' : 'without-percentages'}`}>
								{renderPercentage(awayWinPredictionPercent, awayPercentColor, actualOutcomeKey && actualOutcomeKey !== 'away')}
								<input
									type="number"
									className={`score-input ${statusClass === 'match-status-in-progress'
										? 'in-progress-input'
										: (statusClass === 'match-status-finished' || statusClass === 'match-status-postponed')
											? 'finished-input'
											: ''
										}`}
									value={predictedAwayScore}
									onClick={(e) => e.stopPropagation()}
									onChange={(e) => !isPast && !isDisabled && !competitiveTipsLocked && onInputChange('predictedAwayScore', e.target.value)}
									disabled={isPast || isDisabled || isScoutMode || !isMember || competitiveTipsLocked}
									min="0"
								/>
								{renderTendencyPoints('away', match.awayTeamPoints, t('matchCard.tendencyAway', { defaultValue: 'Body za správně tipnutou výhru hostů' }))}
							</div>

							<div
								className={`team-block ${!isLoading && !isDisabled && !isScoutMode ? 'clickable' : ''} ${isFinished && !isOvertimeFinish && (homeWins || isNonOvertimeDraw) ? 'team-lost' : ''}`}
								onClick={(e) => { e.stopPropagation(); handleTeamClick(match.awayTeam); }}
							>
								<TeamBadge
									teamId={match.awayTeam.id}
									teamName={match.awayTeam.name}
									isPostponed={isPostponed}
									isFlag={isInternational === true}
									plain={isInternational !== true}
									shadowColor={awayBgRgb}
									grayscale={isOvertimeFinish}
								/>
								<div className="team-name-row">
									<span className={`team-name ${awayWins ? 'team-name-winner' : ''}`}>
										{shouldTranslateTeams
											? t(`teams.${match.awayTeam.name}`, { defaultValue: match.awayTeam.name })
											: match.awayTeam.name
										}
									</span>
									{advancingSide === 'away' && (
										<span
											className="advancing-arrow"
											title={t('matchCard.advances', { defaultValue: 'Postupuje' })}
											onClick={(e) => e.stopPropagation()}
										>
											<FaArrowUp />
										</span>
									)}
									{renderRedCard(redCards.away)}
								</div>

								{showAdvancingSelection && (
									<div className="advancing-selection" onClick={(e) => e.stopPropagation()}>
										<label className={prediction?.advancingTeamId && prediction.advancingTeamId !== match.awayTeam.id ? 'dimmed' : ''}>
											<input
												type="radio"
												name={`advancing-${match.id}`}
												checked={prediction?.advancingTeamId === match.awayTeam.id}
												onChange={() => !isPast && !isDisabled && onInputChange('advancingTeamId', match.awayTeam.id)}
												disabled={isPast || isDisabled || isScoutMode}
											/>
											{t('matchCard.advances', { defaultValue: 'Postoupí' })}
										</label>
									</div>
								)}
							</div>
						</div>
					</div>

					{!isNotStarted && (
						<div className="match-status-text">
							<span>{t(`matchStatus.${match.matchStatus}`, { defaultValue: match.matchStatus })}</span>
							{match.matchStatus === 'PEN' && match.penaltyScoreHome != null && match.penaltyScoreAway != null && (
								<span style={{ marginLeft: '4px' }}>
									({match.penaltyScoreHome}:{match.penaltyScoreAway})
								</span>
							)}
							{(match.matchStatus === 'AET' || match.matchStatus === 'AOT') &&
								match.regularTimeScoreHome != null && match.regularTimeScoreAway != null && (
									<span style={{ marginLeft: '4px' }}>
										({match.regularTimeScoreHome}:{match.regularTimeScoreAway})
									</span>
								)}
							{statusClass === 'match-status-in-progress' && match.progress > 0 && (
								<span className="match-progress" style={{ marginLeft: '4px' }}>
									({match.progress}<span className="blinking-minute">′</span>)
								</span>
							)}
						</div>
					)}

					{/* Původní checkbox schováme pro fotbal – u odvety i jednorázového zápasu řešíme prodloužení přes radio buttony postupu, které se ukážou automaticky při tipu na remízu */}
					{match.canGoToOvertime && !isFootball && (
						<div className={`overtime-draw-checkbox ${isPast ? 'past' : ''}`}>
							<label onClick={(e) => e.stopPropagation()}>
								<input
									type="checkbox"
									checked={!!prediction?.isDrawInRegularTime}
									onChange={(e) => !isPast && !isDisabled && onInputChange('isDrawInRegularTime', e.target.checked)}
									disabled={isPast || isDisabled || isScoutMode}
								/>
								{" "}{isFootball
									? t('leagueDetails.goesToOvertime')
									: t('leagueDetails.drawInRegularTime')
								}
							</label>
						</div>
					)}

					{competitiveBreakdown ? (
						<div className={`user-points-row competitive-points-row ${competitiveBreakdown.isExactHit ? 'exact-hit' : ''}`}>
							<div className="competitive-points-item" title={t('matchCard.tendencyHome', { defaultValue: 'Body za tendenci' })}>
								<span className="competitive-points-label">{t('matchCard.competitive.matchLabel', { defaultValue: 'Zápas' })}</span>
								<span className={`competitive-points-badge tendency-badge ${competitiveBreakdown.correctTendency ? 'earned' : 'missed'}`}>
									+{competitiveBreakdown.tendencyPts}
								</span>
							</div>
							{competitiveBreakdown.isExactHit && (
								<div className="competitive-points-item" title={t('matchCard.competitive.exactScoreLabel', { defaultValue: 'Přesné skóre' })}>
									<span className="competitive-points-label">{t('matchCard.competitive.exactScoreLabel', { defaultValue: 'Přesné skóre' })}</span>
									<span className="competitive-points-badge exact-badge earned">
										🎯 +{competitiveBreakdown.exactPts}
									</span>
								</div>
							)}
						</div>
					) : (userPoints !== null && typeof userPoints !== 'undefined' && (
						<div className="user-points-row">
							<span className="user-points-label">{t('leagueDetails.yourPoints')}:</span>
							<div
								className="points-tooltip-container"
								onMouseEnter={() => setShowTooltip(true)}
								onMouseLeave={() => setShowTooltip(false)}
							>
								<span className={`user-points-value ${pointsClass}`}>
									{basePoints === 10 ? `🎯 ${userPoints}` : userPoints}
								</span>
								{showTooltip && (
									<div className="points-tooltip">
										{getTooltipText()}
									</div>
								)}
							</div>
						</div>
					))}

					{(!isPast && isMember && competitiveTipsLocked) && (
						<div className="tip-button-row">
							<div className="competitive-tips-locked" onClick={(e) => e.stopPropagation()}>
								<FaInfoCircle />
								<span>
									{t('matchCard.competitive.pointsPending', {
										defaultValue: 'Tipování se otevře, až budou známy body za tendence zápasu'
									})}
								</span>
							</div>
						</div>
					)}

					{(!isPast && isMember && !competitiveTipsLocked) && (
						<div className="tip-button-row">
							<button
								className={`tip-button ${isLoading ? 'loading' : ''}`}
								onClick={(e) => { e.stopPropagation(); onSubmitPrediction(e); }}
								disabled={isButtonDisabled}
							>
								<div className="tip-button-content">
									{isLoading && <span className="button-spinner"></span>}
									<span>{getButtonText()}</span>
								</div>
							</button>
						</div>
					)}
				</div>

				{loadedAnalysis && isAnalysisVisible && (
					<div className="ai-analysis-box fade-in" onClick={(e) => e.stopPropagation()}>
						<div className="ai-header">
							<span className="ai-title"><FaMicrochip />{t('aiScout')}</span>
							<button className="ai-close" onClick={() => setIsAnalysisVisible(false)}>
								<FaTimes />
							</button>
						</div>
						<div className="ai-content">
							<TypewriterEffect text={loadedAnalysis} />
						</div>
					</div>
				)}
			</div>

			{teamModalInfo && (
				<TeamMatchesModal
					open={isModalOpen}
					teamId={teamModalInfo.id}
					teamName={teamModalInfo.name}
					onClose={handleCloseModal}
				/>
			)}

			<MatchStatisticsModal
				open={statsModalOpen}
				matchId={match.id}
				homeTeam={match.homeTeam}
				awayTeam={match.awayTeam}
				scoreHome={match.scoreHome}
				scoreAway={match.scoreAway}
				onClose={() => setStatsModalOpen(false)}
				hasStatistics={hasStatisticsAvailable}
				leagueId={leagueId}
				showTips={canSeeOtherTips}
				scoringType={scoringType}
				multiplier={multiplier}
				status={{
					matchStatus: match.matchStatus,
					penaltyScoreHome: match.penaltyScoreHome,
					penaltyScoreAway: match.penaltyScoreAway,
					regularTimeScoreHome: match.regularTimeScoreHome,
					regularTimeScoreAway: match.regularTimeScoreAway,
					progress: match.progress,
					inProgress: statusClass === 'match-status-in-progress'
				}}
			/>
		</>
	);
};

export default MatchCard;